/* eslint-disable no-void */
import { webAnalytics as wa } from '@principal/web-analytics';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CookiesProvider } from 'react-cookie';
import { useAuth } from 'react-oidc-context';
import { Route, Switch } from 'react-router-dom';
import HubApi from './api/hub-api';
import Footer from './footer/Footer/Footer';
import Hub from './Hub';
import Navbar from './navbar/Navbar/Navbar';
import Operations from './Operations';
import LoadingPage from './shared/components/Loading/LoadingPage';
import useScript from './shared/hooks/useScript';
import HubView from './shared/models/HubView';

const getNormalizedPath = (): string => {
  const path = window.location.pathname.replace(/\/index\.html$/, '/');
  return path === '' ? '/' : path;
};

const App: React.FC = () => {
  const [view, setView] = useState<HubView | undefined>();
  const [loggedIn, setLoggedIn] = useState(true);
  const [hasTriedLogin, setHasTriedLogin] = useState(false);
  const hasFetchedHubView = useRef(false);
  const {
    user,
    settings,
    removeUser,
    signoutRedirect,
    signinRedirect,
    isAuthenticated,
    activeNavigator,
    isLoading,
    error,
    events,
    signinSilent,
  } = useAuth();

  useScript({
    url: `https://js-cdn.dynatrace.com/jstag/16ab023090d/${process.env.REACT_APP_DYNATRACE_ENV}_complete.js`,
    type: 'text/javascript',
    crossOrigin: 'anonymous',
  });

  const logout = useCallback(async () => {
    const args = {
      id_token_hint: user?.id_token,
      client_id: settings.client_id,
      extraQueryParams: {
        ...(process.env.PUBLIC_URL === '/personas' && { logout_uri: settings.redirect_uri }),
      },
    };
    await removeUser();
    await signoutRedirect(args);
    setLoggedIn(false);
  }, [removeUser, signoutRedirect, user?.id_token, settings.client_id, settings.redirect_uri]);

  const login = useCallback(async () => {
    await signinRedirect();
  }, [signinRedirect]);

  useEffect(() => {
    const fetch = async () => {
      const newView = await HubApi.get();
      setView(newView);
      setHasTriedLogin(true);
    };
    if (isAuthenticated && !hasFetchedHubView.current) {
      hasFetchedHubView.current = true;
      fetch();
    }
    if (!isAuthenticated) {
      hasFetchedHubView.current = false;
    }
    if (!isAuthenticated && !activeNavigator && !isLoading && !error && !hasTriedLogin) {
      login();
    }
  }, [isAuthenticated, activeNavigator, isLoading, error, login, hasTriedLogin]);

  useEffect(() => {
    const removeListener = events.addAccessTokenExpiring(async () => {
      const userResponse = await signinSilent();
      if (!userResponse) {
        hasFetchedHubView.current = false;
        await logout();
      }
    });
    return removeListener;
  }, [events, signinSilent, logout]);

  useEffect(() => {
    wa.trackPageView({
      virtualpagedetails: {
        pagepath: getNormalizedPath(),
        pagetitle: document.title,
      },
      content: {
        businessline: 'benefits-and-protection',
        purpose: 'engagement',
        intendedaudience: 'financial-professional',
      },
      page: {
        documenttype: 'single-page-application',
        language: 'en-us',
        appid: 'appid-14107667',
      },
    });
  }, []);

  if (isAuthenticated) {
    return (
      <CookiesProvider>
        <>
          <Navbar
            user={view?.user}
            feedback={view?._links?.feedback}
            loggedIn={loggedIn}
            logout={logout}
            login={login}
          />
          {view?._links !== undefined ? (
            <Switch>
              <Route path="/operations">
                <Operations
                  user={view.user}
                  securityProfilesLink={view._links.securityProfiles}
                  notificationsLink={view._links.notifications}
                  saveFirmContactLink={view._links.saveFirmContact}
                  saveFirmDelegateLink={view._links.saveFirmDelegate}
                />
              </Route>
              <Route exact path="/">
                <Hub view={view} />
              </Route>
            </Switch>
          ) : (
            <LoadingPage />
          )}
          <Footer feedbackHref={view?._links?.feedback?.href} />
        </>
      </CookiesProvider>
    );
  }
  return <div data-testid="non-auth-view" />;
};
export default App;
